
import { Building, CertificationStatus } from "@/types/portfolio";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Trash2, Clock, CheckCircle, AlertTriangle, Building as BuildingIcon, MapPin } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

interface BuildingCardProps {
  building: Building;
  onDelete: (id: string) => void;
}

export const BuildingCard = ({ building, onDelete }: BuildingCardProps) => {
  const getCertificationIcon = (status: CertificationStatus) => {
    switch (status) {
      case "pending":
        return <Clock className="h-4 w-4 mr-1" />;
      case "in_progress":
        return <Clock className="h-4 w-4 mr-1 text-blue-500" />;
      case "certified":
        return <CheckCircle className="h-4 w-4 mr-1 text-green-500" />;
      case "rejected":
        return <AlertTriangle className="h-4 w-4 mr-1 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 mr-1" />;
    }
  };

  const getCertificationColor = (status: CertificationStatus) => {
    switch (status) {
      case "pending":
        return "bg-gray-100 text-gray-800";
      case "in_progress":
        return "bg-blue-100 text-blue-800";
      case "certified":
        return "bg-green-100 text-green-800";
      case "rejected":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <Card className="h-full flex flex-col">
      <CardHeader>
        <div className="flex justify-between items-start">
          <div className="flex items-center gap-2">
            <BuildingIcon className="h-5 w-5 text-muted-foreground" />
            <CardTitle className="text-lg">{building.name}</CardTitle>
          </div>
          <span
            className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${getCertificationColor(
              building.certification_status
            )}`}
          >
            {getCertificationIcon(building.certification_status)}
            {building.certification_status.replace("_", " ")}
          </span>
        </div>
      </CardHeader>
      <CardContent className="flex-grow">
        <p className="text-sm text-muted-foreground mb-2">
          {building.description || "No description provided."}
        </p>
        {building.address && (
          <div className="flex items-center gap-1 text-sm text-muted-foreground mb-2">
            <MapPin className="h-4 w-4" />
            <span>{building.address}</span>
          </div>
        )}
        {building.square_footage && (
          <p className="text-sm text-muted-foreground mb-2">
            <span className="font-medium">Size:</span> {building.square_footage.toLocaleString()} sq ft
          </p>
        )}
        {building.year_built && (
          <p className="text-sm text-muted-foreground mb-2">
            <span className="font-medium">Built:</span> {building.year_built}
          </p>
        )}
        <p className="text-xs text-muted-foreground mt-4">
          Added {formatDistanceToNow(new Date(building.created_at), { addSuffix: true })}
        </p>
      </CardContent>
      <CardFooter className="pt-2 border-t flex justify-end">
        <Button
          variant="outline"
          size="icon"
          className="text-destructive"
          onClick={() => onDelete(building.id)}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </CardFooter>
    </Card>
  );
};
